import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Badge, Button, Card, Col, Form, ListGroup, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import {
  fetchAdminSettingsOverview,
  fetchAdminSettingsReadiness,
  fetchAdminSettingsCompanyProfile,
  routeAdminSettingsOnboardingPackage,
  updateAdminSettingsCompanyProfile,
  type AdminSettingsOverviewResponse,
  type AdminSettingsReadinessResponse,
} from './api';
import type { CompanyProfile } from '../admin/clients/api';
import {
  CompanyInfoTab,
  emptyBrandingDraftState,
  emptyProfileFormState,
  profileToFormState,
  type BrandingDraftState,
  type CompanyProfileFormState,
  type FieldErrors,
} from '../admin/clients/clients-page';
import { adminSettingsSections, type AdminSettingsSection } from './config';
import ManagementTableScaffold, {
  isManagementTableSectionId,
} from './management-table-scaffold';
import DynamicViewsWorkspace from './dynamic-views-workspace';
import SharedCompanyResourceWorkspace from './shared-company-resource-workspace';
import { SharedScopeAdminWorkspace } from '../schedule-settings/shared-scope-workspace';
import PortfolioSummaryCard from '../shared/portfolio-summary-card';
import './admin-settings-page.scss';

type AdminSettingsPageProps = {
  sectionId: string;
};

const toneClassMap: Record<AdminSettingsSection['tone'], string> = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  info: 'info',
};

const groupStatusTone = (status: AdminSettingsOverviewResponse['group_portfolio'][number]['status']) => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Draft':
    case 'Onboarding':
      return 'warning';
    case 'Needs attention':
      return 'danger';
    case 'Archived':
    case 'Inactive':
      return 'dark';
    default:
      return 'secondary';
  }
};

const hasCompanyAdminScope = () => {
  const bootstrap = getCaresShellBootstrap();

  return bootstrap.canAccessAdmin || bootstrap.tenantCompanies.some((company) =>
    company.role_keys.includes('company_admin')
  );
};

const roleResponsibilities = [
  {
    role: 'Platform/System Admin',
    description: 'Creates the company tenant, designates the first Company Admin, and supports platform-wide recovery, audit, and settings.',
  },
  {
    role: 'Company Admin',
    description: 'Confirms company profile, sets up hospitals/locations, Departments and Schedule Groups, and assigns Scheduler Admins.',
  },
  {
    role: 'Scheduler Admin',
    description: 'Adds users/providers and creates or maintains schedules within assigned service-line, location, or team scope.',
  },
];

const helpQuickActions = [
  { label: 'Company Profile', route: '/admin-settings/company-profile' },
  { label: 'Locations', route: '/admin-settings/locations' },
  { label: 'Departments & Groups', route: '/admin-settings/groups' },
  { label: 'Scheduler Admins', route: '/admin-settings/scheduler-admins' },
  { label: 'Overview & Readiness', route: '/admin-settings/overview' },
];

const adminSettingsPrimaryActions = [
  ...helpQuickActions,
  { label: 'Help Guide', route: '/admin-settings/help-setup-guide' },
];

const setupSequence = [
  {
    key: 'company-profile',
    title: '1. Company Profile',
    route: '/admin-settings/company-profile',
    summary: 'Confirm the company name, contacts, timezone, and identity details that anchor the active setup cycle.',
    completionRule: 'This step is complete when the profile has a saved draft and the core company identity is accurate.',
  },
  {
    key: 'locations',
    title: '2. Locations',
    route: '/admin-settings/locations',
    summary: 'Add hospitals or operating sites where service lines run. A location can later supply inherited color cues for related service lines.',
    completionRule: 'This step is complete when at least one location exists and draft rows with missing region details have been reviewed.',
  },
  {
    key: 'groups',
    title: '3. Departments & Groups',
    route: '/admin-settings/groups',
    summary: 'Create organizational Departments, then assign each independent scheduling unit as a child Schedule Group.',
    completionRule: 'This step is complete when the shared company Department structure is accurate for downstream scheduling work.',
  },
  {
    key: 'scheduler-admins',
    title: '4. Scheduler Admins',
    route: '/admin-settings/scheduler-admins',
    summary: 'Assign Scheduler Admin ownership by Schedule Type + Service Line, optionally narrowed by location or department.',
    completionRule: 'Company activation can proceed once every active service line has pending or active coverage. First-draft readiness requires active coverage.',
  },
  {
    key: 'readiness',
    title: '5. Readiness',
    route: '/admin-settings/overview',
    summary: 'Review blockers, handoff status, and dynamic relationships before declaring the company ready for activation or first draft.',
    completionRule: 'Use this view to separate activation-ready setup from deeper draft-schedule readiness that still belongs to Scheduler Admin follow-through.',
  },
];

const helpTopics = [
  {
    id: 'role-overview',
    title: 'Company Admin role overview',
    body: 'Company Admins own company structure, setup quality, and Scheduler Admin assignment. They do not manage platform-wide access or server behavior.',
    bullets: [
      'Platform/System Admin creates the company and names the first Company Admin.',
      'Company Admin manages profile, locations, Departments, Schedule Groups, and Scheduler Admin assignment.',
      'Scheduler Admin manages users/providers and schedules within assigned scope after handoff.',
    ],
    route: '/admin-settings/overview',
    actionLabel: 'Open overview',
  },
  {
    id: 'scheduler-admin-scope',
    title: 'Scheduler Admin scope and readiness rules',
    body: 'Scheduler Admin assignments can be granted at a Department level or narrowed to one Schedule Group.',
    bullets: [
      'Department access inherits to its current child Schedule Groups; direct Schedule Group access remains narrow.',
      'Only active Scheduler Admin assignments count toward first-draft readiness.',
      'Deactivated assignments no longer count for either coverage calculation until they are reactivated or replaced.',
    ],
    route: '/admin-settings/scheduler-admins',
    actionLabel: 'Review assignments',
  },
  {
    id: 'imports-and-drafts',
    title: 'Imports, paste, templates, and draft preview',
    body: 'Each management table is built for bulk setup. Use imports and paste to stage rows, then validate and preview before saving them through the active Company Admin Settings workspace.',
    bullets: [
      'Use spreadsheet paste, CSV-like rows, or newline lists when possible; downloadable templates show the expected columns.',
      'Preview validation catches missing required fields, duplicate names/codes, invalid colors, and scope mismatches before rows are committed.',
      'Committed rows save through the Company Admin Settings APIs and remain available when the section is reopened.',
    ],
    route: '/admin-settings/locations',
    actionLabel: 'Open a table',
  },
  {
    id: 'dynamic-views',
    title: 'Dynamic views and readiness review',
    body: 'Dynamic views reuse the same Company Admin Settings record set in table, relationship, overview, and readiness modes so admins can spot blockers without duplicate data entry.',
    bullets: [
      'Relationship view maps Locations → Departments → Schedule Groups → Scheduler Admins.',
      'Overview view gives a quick audit across locations, service lines, departments, and Scheduler Admins.',
      'Readiness view calls out activation blockers separately from first-draft readiness gaps.',
    ],
    route: '/admin-settings/overview',
    actionLabel: 'Open overview',
  },
  {
    id: 'color-coding',
    title: 'Color coding and accessibility',
    body: 'Colors are admin-defined metadata for locations, schedule types, service lines, and departments. They are never the only signal.',
    bullets: [
      'Use presets or a hex value to set a color, then review the preview chip and contrast guidance before saving.',
      'Service lines and departments can inherit color context from parent locations or service lines when a manual color is not set.',
      'Labels, badges, and scope text remain visible even when a color is missing, mixed, or low-contrast.',
    ],
    route: '/admin-settings/groups',
    actionLabel: 'Review color-enabled tables',
  },
  {
    id: 'setup-cycle',
    title: 'One active setup cycle, reopen, and archive behavior',
    body: 'Only one active setup cycle should remain open for a company. Historical cycles should be archived, completed, or abandoned by platform tooling before a new one starts.',
    bullets: [
      'Use each live section to update company structure and review readiness without relying on sample progress widgets.',
      'Revisit a section when structure changes require another pass.',
      'Treat cycle archive and replacement as controlled backend history, not an inline authenticated-route action.',
    ],
    route: '/admin-settings/overview',
    actionLabel: 'Review overview',
  },
];

const stLukesExamples = [
  'Company: St. Luke’s',
  'Hospitals / locations: St. Luke’s Main Hospital, St. Luke’s East, St. Luke’s West',
  'Schedule type: Clinician',
  'Service lines: Trauma, Acute Care Surgery, General Surgery, ICU / Critical Care',
  'Companywide example: General Surgery can be marked Companywide instead of tying it to one site list.',
  'Scheduler Admin scope: Trauma service line, optionally narrowed by hospital/location or team',
  'Schedules under Trauma: Trauma Attending Schedule, Trauma Resident/APP Schedule, Trauma Backup Call Schedule',
];

const faqItems = [
  {
    question: 'Can one service line have multiple schedules?',
    answer: 'Yes. Trauma can have attending, resident/APP, backup call, clinic, or other schedules under the same service line.',
  },
  {
    question: 'Can one Scheduler Admin manage multiple service lines?',
    answer: 'Yes, if each scope is assigned explicitly.',
  },
  {
    question: 'Can a service line span multiple hospitals or be companywide?',
    answer: 'Yes. Attach one or more hospitals/locations, or deliberately mark the service line Companywide.',
  },
  {
    question: 'Should I delete or deactivate setup records?',
    answer: 'Deactivate or archive records that have already been used by assignments or schedules. Hard delete should stay limited to unused draft rows.',
  },
  {
    question: 'Can setup change after onboarding is complete?',
    answer: 'Yes. These sections remain ongoing management tables after the initial setup pass.',
  },
];

const AdminSettingsPrimaryActionBar: FC<{
  currentPath: string;
  nextRecommendedAction: string;
}> = ({ currentPath, nextRecommendedAction }) => (
  <Card className="custom-card border shadow-none mb-4 d-md-none">
    <Card.Body className="p-3">
      <div className="d-flex flex-column gap-3">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
          <div>
            <p className="text-muted text-uppercase fs-12 mb-1">Primary actions</p>
            <p className="text-muted mb-0 fs-13">{nextRecommendedAction}</p>
          </div>
          <Badge bg="" className="bg-light text-muted align-self-start align-self-lg-center">
            Mobile quick access
          </Badge>
        </div>

        <div className="d-flex gap-2 flex-nowrap overflow-auto pb-1">
          {adminSettingsPrimaryActions.map((action) => {
            const isActive = action.route === currentPath;

            return (
              <Link
                key={action.route}
                to={action.route}
                className={`btn btn-sm admin-settings-primary-action ${isActive ? 'btn-primary' : 'btn-light'} text-nowrap`}
              >
                {action.label}
              </Link>
            );
          })}
        </div>
      </div>
    </Card.Body>
  </Card>
);

const HelpSetupGuide: FC = () => (
  <Row className="g-4">
    <Col xl={12}>
      <Card className="custom-card overflow-hidden">
        <Card.Body className="p-4 p-xxl-5">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
            <div>
              <Badge bg="" className="bg-info-transparent text-info mb-3">
                Company Admin reference
              </Badge>
              <h2 className="mb-2">Help &amp; Setup Guide</h2>
              <p className="text-muted mb-0">
                Use this route as the durable onboarding reference for setup sequence, Companywide service lines, Scheduler Admin ownership, imports, readiness, and cycle rules.
              </p>
            </div>
            <div className="text-xl-end">
              <p className="fs-12 text-uppercase text-muted mb-2">Recommended flow</p>
              <h4 className="mb-1">Profile to readiness</h4>
              <p className="text-muted mb-0 fs-13">Follow the same order used by the sidebar sections and readiness workflow.</p>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-4">
            {helpQuickActions.map((action) => (
              <Link key={action.route} to={action.route} className="btn btn-sm btn-light">
                {action.label}
              </Link>
            ))}
          </div>

          <Row className="g-3">
            {roleResponsibilities.map((item) => (
              <Col md={4} key={item.role}>
                <Card className="custom-card border shadow-none mb-0 h-100">
                  <Card.Body>
                    <p className="text-muted fs-12 text-uppercase mb-2">Role</p>
                    <h5 className="mb-2">{item.role}</h5>
                    <p className="text-muted mb-0">{item.description}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>
    </Col>

    <Col xxl={8}>
      <Card className="custom-card mb-4">
        <Card.Header>
          <div className="card-title">Setup Sequence</div>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            {setupSequence.map((step) => (
              <Col md={6} key={step.key}>
                <Card className="custom-card border shadow-none mb-0 h-100">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                      <h5 className="mb-0">{step.title}</h5>
                      <Link to={step.route} className="btn btn-sm btn-outline-primary">
                        Open
                      </Link>
                    </div>
                    <p className="text-muted mb-2">{step.summary}</p>
                    <p className="text-muted fs-13 mb-0">{step.completionRule}</p>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      <Card className="custom-card">
        <Card.Header>
          <div className="card-title">Guide Topics</div>
        </Card.Header>
        <Card.Body>
          <Accordion defaultActiveKey={['role-overview', 'scheduler-admin-scope']} alwaysOpen>
            {helpTopics.map((topic) => (
              <Accordion.Item eventKey={topic.id} key={topic.id}>
                <Accordion.Header>{topic.title}</Accordion.Header>
                <Accordion.Body>
                  <p className="text-muted">{topic.body}</p>
                  <ul className="mb-3 ps-3">
                    {topic.bullets.map((bullet) => (
                      <li className="text-muted mb-2" key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                  <Link to={topic.route} className="btn btn-sm btn-outline-primary">
                    {topic.actionLabel}
                  </Link>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>
    </Col>

    <Col xxl={4}>
      <Card className="custom-card mb-4">
        <Card.Header>
          <div className="card-title">Readiness Rules</div>
        </Card.Header>
        <Card.Body>
          <Alert variant="success" className="mb-3">
            Pending Scheduler Admin invites count for company activation coverage.
          </Alert>
          <Alert variant="warning" className="mb-3">
            Only active Scheduler Admin assignments count for first-draft readiness.
          </Alert>
          <Alert variant="info" className="mb-0">
            Company profile, management tables, readiness summaries, and dynamic setup views now read/write through the Company Admin Settings APIs. Invite delivery and downstream provider/schedule build workflows remain separate follow-up work.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="custom-card mb-4">
        <Card.Header>
          <div className="card-title">St. Luke’s Example</div>
        </Card.Header>
        <Card.Body>
          <ListGroup variant="flush">
            {stLukesExamples.map((item) => (
              <ListGroup.Item key={item} className="px-0">
                <div className="d-flex align-items-start gap-2">
                  <span className="badge bg-info-transparent text-info mt-1">
                    <i className="ri-information-line"></i>
                  </span>
                  <span className="text-muted">{item}</span>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>

      <Card className="custom-card">
        <Card.Header>
          <div className="card-title">FAQ</div>
        </Card.Header>
        <Card.Body>
          <ListGroup variant="flush">
            {faqItems.map((item) => (
              <ListGroup.Item className="px-0" key={item.question}>
                <p className="fw-semibold mb-1">{item.question}</p>
                <p className="text-muted mb-0">{item.answer}</p>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

const AdminSettingsPage: FC<AdminSettingsPageProps> = ({ sectionId }) => {
  const bootstrap = getCaresShellBootstrap();
  const location = useLocation();
  const tenantQuery = location.search.startsWith('?') ? location.search.slice(1) : location.search;
  const [overview, setOverview] = useState<AdminSettingsOverviewResponse | null>(null);
  const [readiness, setReadiness] = useState<AdminSettingsReadinessResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [profileFormState, setProfileFormState] = useState<CompanyProfileFormState>(emptyProfileFormState);
  const [brandingDraft, setBrandingDraft] = useState<BrandingDraftState>(emptyBrandingDraftState);
  const [profileFieldErrors, setProfileFieldErrors] = useState<FieldErrors>({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [routingSelections, setRoutingSelections] = useState<Record<string, string>>({});
  const [routingPackageId, setRoutingPackageId] = useState<string | null>(null);
  const [routingMessage, setRoutingMessage] = useState<string | null>(null);
  const currentSection =
    adminSettingsSections.find((section) => section.id === sectionId) ?? adminSettingsSections[0];
  const isPrimarySetupSection = ['company-profile', 'locations', 'groups', 'scheduler-admins'].includes(currentSection.id);
  const showMobilePrimaryActions = currentSection.id !== 'overview' && currentSection.id !== 'help-setup-guide';
  const nextRecommendedAction = overview?.next_recommended_action ?? currentSection.readinessLabel;
  const departmentPortfolio = useMemo(() => {
    const groups = overview?.group_portfolio ?? [];
    const buckets = new Map<string, { id: string; name: string; groups: typeof groups }>();

    groups.forEach(group => {
      const id = group.department ? String(group.department.id) : 'unassigned';
      const bucket = buckets.get(id) ?? {
        id,
        name: group.department?.name ?? 'Unassigned Schedule Groups',
        groups: [],
      };
      bucket.groups.push(group);
      buckets.set(id, bucket);
    });

    return Array.from(buckets.values()).sort((left, right) => {
      if (left.id === 'unassigned') return 1;
      if (right.id === 'unassigned') return -1;
      return left.name.localeCompare(right.name);
    });
  }, [overview?.group_portfolio]);
  const departmentCoverage = useMemo(() => new Map(
    (overview?.department_hierarchy ?? []).map((department) => [String(department.id), department])
  ), [overview?.department_hierarchy]);

  const loadPageData = async (shouldCancel?: () => boolean) => {
    setLoading(true);
    setRequestError(null);

    try {
      const [overviewResponse, readinessResponse, companyProfileResponse] = await Promise.all([
        fetchAdminSettingsOverview(tenantQuery),
        fetchAdminSettingsReadiness(tenantQuery),
        fetchAdminSettingsCompanyProfile(tenantQuery),
      ]);

      if (shouldCancel?.()) {
        return;
      }

      setOverview(overviewResponse);
      setReadiness(readinessResponse);
      setCompanyProfile(companyProfileResponse);
      setProfileFormState(profileToFormState(companyProfileResponse));
      setBrandingDraft(emptyBrandingDraftState);
      setProfileFieldErrors({});
    } catch (error) {
      if (!shouldCancel?.()) {
        setRequestError(error instanceof Error ? error.message : 'Unable to load Company Admin Settings.');
        setOverview(null);
        setReadiness(null);
        setCompanyProfile(null);
      }
    } finally {
      if (!shouldCancel?.()) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    let cancelled = false;

    void loadPageData(() => cancelled);

    return () => {
      cancelled = true;
    };
  }, [tenantQuery]);

  const handleProfileSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!companyProfile) {
      return;
    }

    setProfileSaving(true);
    setProfileMessage(null);
    setRequestError(null);
    setProfileFieldErrors({});

    try {
      const payload = new FormData();
      payload.append('_method', 'PATCH');
      Object.entries(profileFormState).forEach(([key, value]) => payload.append(key, value.trim()));
      if (brandingDraft.logoFile) payload.append('logo', brandingDraft.logoFile);
      if (brandingDraft.logoMarkFile) payload.append('logo_mark', brandingDraft.logoMarkFile);
      if (brandingDraft.logoMarkUrl.trim()) payload.append('logo_mark_url', brandingDraft.logoMarkUrl.trim());
      if (brandingDraft.removeLogo) payload.append('remove_logo', '1');
      if (brandingDraft.removeLogoMark) payload.append('remove_logo_mark', '1');

      const nextProfile = await updateAdminSettingsCompanyProfile(tenantQuery, payload);
      setCompanyProfile(nextProfile);
      setProfileFormState(profileToFormState(nextProfile));
      setBrandingDraft(emptyBrandingDraftState);
      setOverview((currentOverview) => currentOverview ? {
        ...currentOverview,
        selected_company: {
          ...currentOverview.selected_company,
          logo_url: nextProfile.branding.logo_url,
          logo_mark_url: nextProfile.branding.logo_mark_url,
        },
      } : currentOverview);
      setProfileMessage('Company profile saved. Changes are shared with CARES Administration.');
    } catch (error) {
      const validation = error instanceof Error
        ? ((error as Error & { validation?: { errors?: Record<string, string[]> } }).validation?.errors ?? {})
        : {};
      const fieldErrors = {} as FieldErrors;
      Object.entries(validation).forEach(([key, messages]) => {
        if (key in emptyProfileFormState) fieldErrors[key as keyof CompanyProfileFormState] = messages[0];
      });
      setProfileFieldErrors(fieldErrors);
      setRequestError(error instanceof Error ? error.message : 'Unable to save company profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePackageRouting = async (packageKey: string) => {
    const groupId = Number(routingSelections[packageKey] ?? 0);
    if (!groupId) {
      setRequestError('Select a Schedule Group before routing this onboarding package.');
      return;
    }

    const draftPackage = overview?.onboarding_draft_routing.packages.find(item => item.package_key === packageKey);
    const targetGroup = overview?.onboarding_draft_routing.schedule_groups.find(item => item.id === groupId);
    if (!window.confirm(
      `Assign ${draftPackage?.name ?? 'this onboarding package'} to ${targetGroup?.name ?? 'the selected Schedule Group'}? This will make the file and any review drafts available to that group without approving or activating them.`
    )) {
      return;
    }

    setRoutingPackageId(packageKey);
    setRoutingMessage(null);
    setRequestError(null);

    try {
      const response = await routeAdminSettingsOnboardingPackage(tenantQuery, packageKey, groupId);
      setRoutingMessage(response.message);
      setRoutingSelections((current) => {
        const next = { ...current };
        delete next[packageKey];
        return next;
      });
      await loadPageData();
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : 'Unable to route the onboarding package.');
    } finally {
      setRoutingPackageId(null);
    }
  };

  if (!bootstrap.authenticated || !hasCompanyAdminScope()) {
    return (
      <>
        <CaresPageHeader homepage="Company Admin" activepage="Company Admin Settings" page="Access Required" />
        <Alert variant="danger" className="custom-card">
          Company Admin Settings is limited to company admins and platform admins.
          {!bootstrap.authenticated ? (
            <>
              {' '}
              <Link to="/login" className="alert-link">
                Sign in
              </Link>{' '}
              to continue.
            </>
          ) : null}
        </Alert>
      </>
    );
  }

  if (currentSection.id === 'overview') {
    return (
      <>
        <CaresPageHeader homepage="Company Admin" activepage="Company Admin Settings" page="Overview" />

        {overview ? (
          <Card className="custom-card mb-3">
            <Card.Body className="p-3 d-flex align-items-center gap-3">
              {overview.selected_company.logo_mark_url || overview.selected_company.logo_url ? (
                <img
                  src={overview.selected_company.logo_mark_url || overview.selected_company.logo_url || ''}
                  alt={`${overview.selected_company.name} logo`}
                  style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                />
              ) : (
                <div className="avatar avatar-lg bg-light text-primary rounded-circle">
                  {overview.selected_company.name.slice(0, 2).toUpperCase()}
                </div>
              )}
              <div>
                <div className="text-muted fs-12 text-uppercase">Company</div>
                <h4 className="mb-0">{overview.selected_company.name}</h4>
              </div>
            </Card.Body>
          </Card>
        ) : null}

        {routingMessage ? <Alert variant="success">{routingMessage}</Alert> : null}

        {overview && overview.onboarding_draft_routing.packages.length > 0 ? (
          <Card className="custom-card mb-3" id="onboarding-draft-routing">
            <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <div className="card-title mb-1">Assign onboarding files</div>
                <p className="text-muted fs-12 mb-0">
                  Route each unassigned spreadsheet or Chronos package to one Schedule Group before schedulers review its contents.
                </p>
              </div>
              <Badge bg="warning">
                {overview.onboarding_draft_routing.packages.length} unassigned {overview.onboarding_draft_routing.packages.length === 1 ? 'package' : 'packages'}
                {overview.onboarding_draft_routing.candidate_count > 0 ? ` · ${overview.onboarding_draft_routing.candidate_count} drafts` : ''}
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {overview.onboarding_draft_routing.packages.length ? (
                <div className="table-responsive">
                  <Table bordered hover className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Import package</th>
                        <th>Version / source</th>
                        <th>Contents</th>
                        <th>Schedule Group</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.onboarding_draft_routing.packages.map((draftPackage) => (
                        <tr key={draftPackage.package_key}>
                          <td>
                            <strong>{draftPackage.name}</strong>
                            <div className="text-muted fs-11">
                              {Object.entries(draftPackage.candidate_kinds).map(([kind, count]) => `${count} ${kind}`).join(' · ')
                                || (draftPackage.package_type === 'spreadsheet' ? 'Spreadsheet import' : 'Review drafts')}
                            </div>
                          </td>
                          <td>
                            <span>{draftPackage.source_version ?? 'No version'}</span>
                            <div className="text-muted fs-11">{draftPackage.source_document ?? 'Chronos package'}</div>
                          </td>
                          <td>
                            <strong>{draftPackage.rows_count} rows</strong>
                            <div className="text-muted fs-11">{draftPackage.candidates_count} review drafts</div>
                          </td>
                          <td style={{ minWidth: '240px' }}>
                            <Form.Select
                              size="sm"
                              aria-label={`Schedule Group for ${draftPackage.name}`}
                              value={routingSelections[draftPackage.package_key] ?? ''}
                              onChange={(event) => setRoutingSelections((current) => ({
                                ...current,
                                [draftPackage.package_key]: event.target.value,
                              }))}
                            >
                              <option value="">Select Schedule Group</option>
                              {overview.onboarding_draft_routing.schedule_groups.map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.department ? `${group.department.name} · ` : ''}{group.name}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={routingPackageId !== null || !routingSelections[draftPackage.package_key]}
                              onClick={() => void handlePackageRouting(draftPackage.package_key)}
                            >
                              {routingPackageId === draftPackage.package_key ? <><Spinner animation="border" size="sm" className="me-2" />Assigning…</> : 'Assign drafts'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <Alert variant="warning" className="m-3 mb-0">
                  Unassigned drafts exist without an import package and cannot be safely routed in bulk.
                </Alert>
              )}
              <div className="border-top p-3 text-muted fs-12">
                Assignment is atomic and auditable. It does not approve, activate, or publish any draft.
              </div>
            </Card.Body>
          </Card>
        ) : null}

        {requestError ? (
          <Alert variant="danger" className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <span>{requestError}</span>
            <Button variant="danger" onClick={() => void loadPageData()}>Retry</Button>
          </Alert>
        ) : null}

        {loading ? (
          <Card className="custom-card">
            <Card.Body className="p-4 d-flex align-items-center gap-3 text-muted">
              <Spinner animation="border" size="sm" /> Loading schedule-group portfolio…
            </Card.Body>
          </Card>
        ) : overview?.group_portfolio.length === 0 ? (
          <Alert variant="warning">No schedule groups are configured for {overview.selected_company.name}.</Alert>
        ) : overview ? (
          <div className="d-flex flex-column gap-4">
            {departmentPortfolio.map(department => (
              <section key={department.id} aria-labelledby={`company-department-${department.id}`}>
                <div className="admin-settings-department-title d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                  <div>
                    <h5 className="mb-1" id={`company-department-${department.id}`}>{department.name}</h5>
                    <span className="text-muted fs-12">{department.groups.length} {department.groups.length === 1 ? 'Schedule Group' : 'Schedule Groups'}</span>
                  </div>
                  <div className="admin-settings-department-title__summary d-flex flex-wrap justify-content-end gap-2">
                    {(() => {
                      const coverage = departmentCoverage.get(department.id);
                      const admins = coverage?.scheduler_admins ?? [];
                      const coveredGroups = coverage?.schedule_groups.filter(group => group.effective_scheduler_admins.length > 0).length ?? 0;
                      const totalGroups = coverage?.schedule_groups.length ?? department.groups.length;

                      return (
                        <>
                          <Badge bg={admins.length ? 'success' : 'warning'} className="admin-settings-department-title__badge">
                            {admins.length ? `Scheduler: ${admins.map(admin => admin.name).join(', ')}` : 'No Department Scheduler Admin'}
                          </Badge>
                          <Badge bg={coveredGroups === totalGroups && totalGroups > 0 ? 'success' : 'warning'} className="admin-settings-department-title__badge">
                            {coveredGroups}/{totalGroups} groups covered
                          </Badge>
                        </>
                      );
                    })()}
                    {Array.from(new Set(department.groups.map(group => group.status))).map(status => (
                      <Badge bg={groupStatusTone(status)} className="admin-settings-department-title__badge" key={status}>{department.groups.filter(group => group.status === status).length} {status}</Badge>
                    ))}
                  </div>
                </div>
                <Row className="g-3 cares-portfolio-grid">
                  {department.groups.map((group) => (
                    <Col sm={6} xl={4} xxl={3} key={group.id}>
                      <PortfolioSummaryCard
                        title={group.name}
                        subtitle={`${overview.selected_company.name} · Schedule Group`}
                        status={group.status}
                        statusTone={groupStatusTone(group.status)}
                        actionTo={`/scheduling/group-schedule?company=${encodeURIComponent(overview.selected_company.slug)}&group=${encodeURIComponent(group.slug)}`}
                        actionLabel="Open schedule group"
                        rows={[{
                          label: 'Providers',
                          total: group.providers.total,
                          active: group.providers.active,
                          onboarding: group.providers.onboarding,
                          pending: group.providers.pending,
                        }]}
                      />
                    </Col>
                  ))}
                </Row>
              </section>
            ))}
            <DynamicViewsWorkspace
              cycleName={overview.active_setup_cycle.name ?? null}
              tenantQuery={tenantQuery}
              readiness={readiness}
              departmentHierarchy={overview.department_hierarchy}
              unassignedScheduleGroups={overview.unassigned_schedule_groups}
            />
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <CaresPageHeader homepage="Company Admin" activepage="Company Admin Settings" page={currentSection.navLabel} />
      {showMobilePrimaryActions ? (
        <AdminSettingsPrimaryActionBar
          currentPath={currentSection.path}
          nextRecommendedAction={nextRecommendedAction}
        />
      ) : null}

      {currentSection.id === 'help-setup-guide' ? <HelpSetupGuide /> : (
      <Row className="g-4">
        {!isPrimarySetupSection ? <Col xl={12}>
          <Card className="custom-card cares-page-hero mb-0">
            <Card.Body className="p-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                <div>
                  <h4 className="mb-1">{currentSection.title}</h4>
                  <p className="text-muted fs-13 mb-0">{currentSection.description}</p>
                </div>
                {['locations', 'groups'].includes(currentSection.id) ? (
                  overview?.selected_company.logo_mark_url || overview?.selected_company.logo_url ? (
                    <img
                      src={overview.selected_company.logo_mark_url || overview.selected_company.logo_url || ''}
                      alt={`${overview.selected_company.name} logo`}
                      style={{ maxWidth: '180px', maxHeight: '52px', objectFit: 'contain' }}
                    />
                  ) : null
                ) : (
                  <div className="d-flex flex-wrap justify-content-xl-end gap-2">
                    {overview ? <Badge bg="" className="bg-light text-muted">{overview.selected_company.name}</Badge> : null}
                    <Badge bg="" className={`bg-${toneClassMap[currentSection.tone]}-transparent text-${toneClassMap[currentSection.tone]}`}>
                      {bootstrap.canAccessAdmin ? 'Platform admin access' : 'Company admin access'}
                    </Badge>
                    {readiness ? (
                      <Badge bg="" className={readiness.overall_readiness.ready ? 'bg-success-transparent text-success' : 'bg-warning-transparent text-warning'}>
                        {readiness.overall_readiness.ready ? 'Ready' : 'Readiness gaps'}
                      </Badge>
                    ) : null}
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col> : null}

        {!isPrimarySetupSection ? <Col xxl={7}>
          <Card className="custom-card h-100">
            <Card.Header>
              <div className="card-title">{currentSection.workspaceTitle}</div>
            </Card.Header>
            <Card.Body>
              <p className="text-muted">{currentSection.workspaceDescription}</p>
              <ListGroup variant="flush">
                {currentSection.highlights.map((item) => (
                  <ListGroup.Item key={item} className="px-0">
                    <div className="d-flex align-items-start gap-2">
                      <span className={`badge bg-${toneClassMap[currentSection.tone]}-transparent text-${toneClassMap[currentSection.tone]} mt-1`}>
                        <i className="ri-check-line"></i>
                      </span>
                      <span className="text-muted">{item}</span>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col> : null}

        {!isPrimarySetupSection ? <Col xxl={5}>
            <Card className="custom-card h-100">
              <Card.Header>
              <div className="card-title">Section Metrics</div>
              </Card.Header>
            <Card.Body>
              <Row className="g-3">
                {currentSection.metrics.map((metric) => (
                  <Col sm={6} key={metric.label}>
                    <Card className="custom-card border shadow-none mb-0 h-100">
                      <Card.Body>
                        <p className="text-muted fs-12 text-uppercase mb-2">{metric.label}</p>
                        <h4 className="mb-0">{metric.value}</h4>
                        <Badge
                          bg=""
                          className={`mt-3 bg-${toneClassMap[metric.tone]}-transparent text-${toneClassMap[metric.tone]}`}
                        >
                          {metric.tone}
                        </Badge>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col> : null}

        {loading ? (
          <Col xl={12}>
            <Alert variant="info" className="custom-card">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading Company Admin Settings data.
            </Alert>
          </Col>
        ) : null}

        {requestError ? (
          <Col xl={12}>
            <Alert variant="danger" className="custom-card">
              {requestError}
            </Alert>
          </Col>
        ) : null}

        {currentSection.id === 'company-profile' && companyProfile ? (
          <Col xxl={12}>
            {profileMessage ? <Alert variant="success">{profileMessage}</Alert> : null}
            <CompanyInfoTab
              profile={companyProfile}
              formState={profileFormState}
              brandingDraft={brandingDraft}
              fieldErrors={profileFieldErrors}
              saving={profileSaving}
              onChange={(field, value) => setProfileFormState((current) => ({ ...current, [field]: value }))}
              onBrandingFileChange={(field, file) => setBrandingDraft((current) => ({ ...current, [field]: file }))}
              onBrandingUrlChange={(value) => setBrandingDraft((current) => ({ ...current, logoMarkUrl: value }))}
              onBrandingRemoveChange={(field, value) => setBrandingDraft((current) => ({ ...current, [field]: value }))}
              onSubmit={handleProfileSave}
            />
          </Col>
        ) : null}

        {['locations', 'groups'].includes(currentSection.id) ? (
          <Col xxl={12}>
            <SharedCompanyResourceWorkspace
              resource={currentSection.id as 'locations' | 'groups'}
              tenantQuery={tenantQuery}
            />
            {currentSection.id === 'groups' ? <SharedScopeAdminWorkspace tenantQuery={tenantQuery} /> : null}
          </Col>
        ) : isManagementTableSectionId(currentSection.id) ? (
          <Col xxl={12}>
            <ManagementTableScaffold
              sectionId={currentSection.id}
              tenantQuery={tenantQuery}
              onSyncChecklist={() => undefined}
            />
          </Col>
        ) : null}

        {!isPrimarySetupSection ? <Col xxl={12}>
          <Card className="custom-card h-100">
            <Card.Header>
              <div className="card-title">Section Guidance</div>
            </Card.Header>
            <Card.Body>
              <ListGroup variant="flush">
                {currentSection.checklist.map((item) => (
                  <ListGroup.Item key={item} className="px-0">
                    <div className="d-flex align-items-start gap-2">
                      <span className="badge bg-light text-muted mt-1">
                        <i className="ri-arrow-right-line"></i>
                      </span>
                      <span className="text-muted">{item}</span>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col> : null}

      </Row>
      )}
    </>
  );
};

export default AdminSettingsPage;
